import React, { useEffect, useRef, useState, memo, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { Copy, Clipboard, Trash2 } from 'lucide-react';
import '@xterm/xterm/css/xterm.css';

const WebTerminal = memo(({ socket, sessionId, dbName }) => {
  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2500);
  }, []);

  // Helper to copy selected text
  const handleCopy = useCallback(() => {
    if (xtermRef.current) {
      const selection = xtermRef.current.getSelection();
      if (selection) {
        navigator.clipboard.writeText(selection);
        showToast('📋 Copié dans le presse-papier');
      } else {
        showToast('⚠️ Aucun texte sélectionné');
      }
    }
  }, [showToast]);

  // Master Paste Sanitizer: Converts ALL newlines (\r and \n) into spaces so NOTHING auto-executes!
  const sanitizeAndPaste = useCallback((rawText) => {
    if (!rawText || !socket || !sessionId) return;
    // Replace all internal newlines with space, strip leading/trailing space
    const cleanText = rawText.replace(/[\r\n]+/g, ' ');
    socket.emit('ssh:input', { sessionId, data: cleanText });
    showToast('📥 Collé sans exécution (Enzel Entrée pour lancer)');
  }, [socket, sessionId, showToast]);

  const handlePasteClick = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        sanitizeAndPaste(text);
      }
    } catch (err) {
      showToast('❌ Erreur de lecture presse-papier');
    }
  }, [sanitizeAndPaste, showToast]);

  const handleClear = useCallback(() => {
    if (xtermRef.current && socket && sessionId) {
      xtermRef.current.clear();
      socket.emit('ssh:input', { sessionId, data: 'clear\n' });
    }
  }, [socket, sessionId]);

  useEffect(() => {
    if (!terminalRef.current || !socket || !sessionId) return;

    // Initialize xterm.js with optimized low-latency settings
    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      scrollback: 5000,
      fontSize: 13,
      fontFamily: 'Consolas, "Courier New", monospace',
      lineHeight: 1.2,
      rightClickSelectsWord: true,
      screenReaderMode: false,
      fastScrollSensitivity: 5,
      theme: {
        background: '#0f172a',
        foreground: '#f8fafc',
        cursor: '#38bdf8',
        selectionBackground: 'rgba(56, 189, 248, 0.4)',
        black: '#1e293b',
        red: '#ef4444',
        green: '#10b981',
        yellow: '#f59e0b',
        blue: '#3b82f6',
        magenta: '#ec4899',
        cyan: '#06b6d4',
        white: '#f8fafc',
        brightBlack: '#64748b',
        brightRed: '#f87171',
        brightGreen: '#34d399',
        brightYellow: '#fbbf24',
        brightBlue: '#60a5fa',
        brightMagenta: '#f472b6',
        brightCyan: '#22d3ee',
        brightWhite: '#ffffff'
      },
    });

    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);

    term.open(terminalRef.current);
    fitAddon.fit();
    
    // Focus automatically so typing works immediately
    setTimeout(() => term.focus(), 100);

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Custom Key Event Interceptor for native PC Ctrl+C / Ctrl+V / Ctrl+Shift+C / Ctrl+Shift+V / Shift+Insert
    term.attachCustomKeyEventHandler((e) => {
      // Ctrl+C or Ctrl+Shift+C (Copy selection if text selected, else pass SIGINT)
      if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyC' || e.key === 'c' || e.key === 'C') && e.type === 'keydown') {
        const selection = term.getSelection();
        if (selection) {
          navigator.clipboard.writeText(selection);
          showToast('📋 Copié dans le presse-papier');
          return false; // Prevent sending SIGINT when copying
        }
      }

      // Ctrl+V or Ctrl+Shift+V or Shift+Insert -> SANITIZE ALL NEWLINES TO PREVENT AUTO-EXECUTION!
      if (
        ((e.ctrlKey || e.metaKey) && (e.code === 'KeyV' || e.key === 'v' || e.key === 'V') && e.type === 'keydown') ||
        (e.shiftKey && e.code === 'Insert' && e.type === 'keydown')
      ) {
        navigator.clipboard.readText().then((text) => {
          if (text) {
            sanitizeAndPaste(text);
          }
        }).catch(() => {
          showToast('❌ Permission presse-papier refusée');
        });
        return false;
      }

      return true;
    });

    // Send terminal size to backend
    socket.emit('ssh:resize', { 
      sessionId, 
      cols: term.cols, 
      rows: term.rows 
    });

    // Handle user input
    const onDataDisposable = term.onData((data) => {
      socket.emit('ssh:input', { sessionId, data });
    });

    // Handle incoming data from backend
    const handleOutput = (data) => {
      term.write(data);
    };

    socket.on('ssh:output', handleOutput);

    // Debounced ResizeObserver to fit terminal smoothly
    let resizeTimeout;
    const resizeObserver = new ResizeObserver(() => {
      if (fitAddonRef.current && xtermRef.current) {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          try {
            const oldCols = xtermRef.current.cols;
            const oldRows = xtermRef.current.rows;
            
            fitAddonRef.current.fit();
            
            if (xtermRef.current.cols !== oldCols || xtermRef.current.rows !== oldRows) {
              socket.emit('ssh:resize', {
                sessionId,
                cols: xtermRef.current.cols,
                rows: xtermRef.current.rows,
              });
            }
          } catch (e) {}
        }, 80);
      }
    });

    resizeObserver.observe(terminalRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      onDataDisposable.dispose();
      socket.off('ssh:output', handleOutput);
      resizeObserver.disconnect();
      term.dispose();
    };
  }, [socket, sessionId, sanitizeAndPaste, showToast]);

  return (
    <div 
      className="w-full h-full flex flex-col bg-[#0f172a] rounded-lg overflow-hidden relative border border-slate-800 shadow-2xl"
      onClick={() => xtermRef.current && xtermRef.current.focus()}
    >
      {/* Quick Action Toolbar */}
      <div className="h-9 px-3 bg-[#1e293b] border-b border-slate-700/60 flex items-center justify-between shrink-0 select-none">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 mr-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80 inline-block" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80 inline-block" />
          </div>
          <span className="text-[11px] font-extrabold text-slate-400 font-mono tracking-wider uppercase">Terminal Shell</span>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleCopy(); }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded transition-colors"
            title="Copier la sélection (Ctrl+C)"
          >
            <Copy size={12} className="text-sky-400" />
            <span>Copier</span>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handlePasteClick(); }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded transition-colors"
            title="Coller le presse-papier (Ctrl+V)"
          >
            <Clipboard size={12} className="text-emerald-400" />
            <span>Coller</span>
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 rounded transition-colors"
            title="Effacer le terminal (Clear)"
          >
            <Trash2 size={12} className="text-rose-400" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="absolute top-11 right-4 z-50 px-3 py-1.5 bg-slate-900/90 text-slate-100 text-xs font-bold rounded-md border border-slate-700 shadow-lg backdrop-blur-sm animate-fade-in flex items-center gap-1.5">
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Terminal Viewport */}
      <div 
        ref={terminalRef} 
        className="flex-1 w-full min-h-0 relative p-2"
        onContextMenu={(e) => {
          e.preventDefault();
          handlePasteClick();
        }}
      />
    </div>
  );
});

export default WebTerminal;
